import React, { useState } from 'react';
import { apiService } from '../services/api';
import { ImportResponse, ImportedEntity } from '../types/api';
import { 
  Upload, 
  FileJson, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Server, 
  Webhook,
  FileText,
  Package,
  Users,
  Building,
  FileCheck,
  Receipt
} from 'lucide-react';
import { ServerDropdown } from '../components';

const DataImport: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<'controller' | 'supervisor' | 'current-supervisor' | 'file' | 'webhook'>('controller');
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [webhookData, setWebhookData] = useState<string>('');
  const [webhookError, setWebhookError] = useState<string>('');

  const handleImportControllerJson = async () => {
    if (!selectedServerId) {
      alert('Please select a server');
      return;
    }

    setIsLoading(true);
    setImportResult(null);
    
    try {
      const response = await apiService.importControllerJson(parseInt(selectedServerId));
      // Ensure response has the expected structure
      const safeResponse: ImportResponse = {
        success: response.success ?? false,
        message: response.message || 'Import completed',
        imported_counts: response.imported_counts || {
          users: 0,
          supervisors: 0,
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        updated_counts: response.updated_counts,
        imported_entities: (response as any).imported_entities,
        errors: response.errors
      };
      setImportResult(safeResponse);
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetail = (error as any).response?.data?.detail || errorMessage;
      setImportResult({
        success: false,
        message: errorDetail || 'Error importing controller.json',
        imported_counts: { 
          users: 0, 
          supervisors: 0, 
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        errors: [errorDetail]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportSupervisorJson = async () => {
    if (!selectedServerId) {
      alert('Please select a server');
      return;
    }

    setIsLoading(true);
    setImportResult(null);
    
    try {
      const response = await apiService.importSupervisorJson(parseInt(selectedServerId));
      // Ensure response has the expected structure
      const safeResponse: ImportResponse = {
        success: response.success ?? false,
        message: response.message || 'Import completed',
        imported_counts: response.imported_counts || {
          users: 0,
          supervisors: 0,
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        updated_counts: response.updated_counts,
        imported_entities: (response as any).imported_entities,
        errors: response.errors
      };
      setImportResult(safeResponse);
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetail = (error as any).response?.data?.detail || errorMessage;
      setImportResult({
        success: false,
        message: errorDetail || 'Error importing supervisor.json',
        imported_counts: { 
          users: 0, 
          supervisors: 0, 
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        errors: [errorDetail]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportCurrentSupervisorJson = async () => {
    if (!selectedServerId) {
      alert('Please select a server');
      return;
    }

    setIsLoading(true);
    setImportResult(null);
    
    try {
      const response = await apiService.importCurrentSupervisorJson(parseInt(selectedServerId));
      // Ensure response has the expected structure
      const safeResponse: ImportResponse = {
        success: response.success ?? false,
        message: response.message || 'Import completed',
        imported_counts: response.imported_counts || {
          users: 0,
          supervisors: 0,
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        updated_counts: response.updated_counts,
        imported_entities: (response as any).imported_entities,
        errors: response.errors
      };
      setImportResult(safeResponse);
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetail = (error as any).response?.data?.detail || errorMessage;
      setImportResult({
        success: false,
        message: errorDetail || 'Error importing current_supervisor.json',
        imported_counts: { 
          users: 0, 
          supervisors: 0, 
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        errors: [errorDetail]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    if (!selectedServerId) {
      alert('Please select a server');
      return;
    }

    setIsLoading(true);
    setImportResult(null);
    
    try {
      const response = await apiService.importFile(selectedFile, parseInt(selectedServerId));
      console.log('Import file response:', response);
      
      // Ensure response has the expected structure
      const safeResponse: ImportResponse = {
        success: response?.success ?? false,
        message: response?.message || 'Import completed',
        imported_counts: response?.imported_counts || {
          users: 0,
          supervisors: 0,
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        updated_counts: response?.updated_counts,
        imported_entities: (response as any)?.imported_entities,
        errors: response?.errors
      };
      console.log('Safe response:', safeResponse);
      setImportResult(safeResponse);
      setSelectedFile(null);
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetail = (error as any).response?.data?.detail || errorMessage;
      setImportResult({
        success: false,
        message: errorDetail || 'Error importing file',
        imported_counts: { 
          users: 0, 
          supervisors: 0, 
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        errors: [errorDetail]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleWebhookImport = async () => {
    if (!selectedServerId) {
      alert('Please select a server');
      return;
    }

    if (!webhookData.trim()) {
      setWebhookError('Please enter JSON data');
      return;
    }

    let parsedData;
    try {
      parsedData = JSON.parse(webhookData);
    } catch (error) {
      setWebhookError('Invalid JSON format');
      return;
    }

    setIsLoading(true);
    setImportResult(null);
    setWebhookError('');
    
    try {
      const response = await apiService.importViaWebhook(parsedData, parseInt(selectedServerId));
      // Ensure response has the expected structure
      const safeResponse: ImportResponse = {
        success: response.success ?? false,
        message: response.message || 'Import completed',
        imported_counts: response.imported_counts || {
          users: 0,
          supervisors: 0,
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        updated_counts: response.updated_counts,
        imported_entities: (response as any).imported_entities,
        errors: response.errors
      };
      setImportResult(safeResponse);
      setWebhookData('');
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetail = (error as any).response?.data?.detail || errorMessage;
      setImportResult({
        success: false,
        message: errorDetail || 'Error importing via webhook',
        imported_counts: { 
          users: 0, 
          supervisors: 0, 
          controllers: 0,
          customers: 0,
          contracts: 0,
          contract_lines: 0
        },
        errors: [errorDetail]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderImportResult = () => {
    if (!importResult) return null;

    try {
      // Add defensive checks and logging
      console.log('Rendering import result:', importResult);
      
      // Ensure we have a valid structure with proper typing
      const safeImportCounts = importResult.imported_counts || {
        users: 0,
        supervisors: 0,
        controllers: 0,
        customers: 0,
        contracts: 0,
        contract_lines: 0
      };
      const safeUpdatedCounts = importResult.updated_counts || {
        users: 0,
        supervisors: 0,
        controllers: 0,
        customers: 0,
        contracts: 0,
        contract_lines: 0
      };

      return (
      <div className={`mt-6 p-6 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex items-start">
          {importResult.success ? (
            <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${importResult.success ? 'text-green-900' : 'text-red-900'}`}>
              {importResult.message || 'Import completed'}
            </h3>
            
            {/* Import counts */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border border-gray-200">
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-blue-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Users</p>
                    <p className="text-lg font-semibold">
                      {safeImportCounts.users || 0}
                      {safeUpdatedCounts.users ? (
                        <span className="text-sm text-gray-500 ml-1">
                          (+{safeUpdatedCounts.users} updated)
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 rounded border border-gray-200">
                <div className="flex items-center">
                  <Building className="w-5 h-5 text-purple-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Supervisors</p>
                    <p className="text-lg font-semibold">
                      {safeImportCounts.supervisors || 0}
                      {safeUpdatedCounts.supervisors ? (
                        <span className="text-sm text-gray-500 ml-1">
                          (+{safeUpdatedCounts.supervisors} updated)
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 rounded border border-gray-200">
                <div className="flex items-center">
                  <Package className="w-5 h-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Controllers</p>
                    <p className="text-lg font-semibold">
                      {safeImportCounts.controllers || 0}
                      {safeUpdatedCounts.controllers ? (
                        <span className="text-sm text-gray-500 ml-1">
                          (+{safeUpdatedCounts.controllers} updated)
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 rounded border border-gray-200">
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-orange-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Customers</p>
                    <p className="text-lg font-semibold">
                      {safeImportCounts.customers || 0}
                      {safeUpdatedCounts.customers ? (
                        <span className="text-sm text-gray-500 ml-1">
                          (+{safeUpdatedCounts.customers} updated)
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 rounded border border-gray-200">
                <div className="flex items-center">
                  <FileCheck className="w-5 h-5 text-indigo-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Contracts</p>
                    <p className="text-lg font-semibold">
                      {safeImportCounts.contracts || 0}
                      {safeUpdatedCounts.contracts ? (
                        <span className="text-sm text-gray-500 ml-1">
                          (+{safeUpdatedCounts.contracts} updated)
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 rounded border border-gray-200">
                <div className="flex items-center">
                  <Receipt className="w-5 h-5 text-teal-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Contract Lines</p>
                    <p className="text-lg font-semibold">
                      {safeImportCounts.contract_lines || 0}
                      {safeUpdatedCounts.contract_lines ? (
                        <span className="text-sm text-gray-500 ml-1">
                          (+{safeUpdatedCounts.contract_lines} updated)
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Imported entities table (like Customers list) */}
            {Array.isArray((importResult as any).imported_entities) && (importResult as any).imported_entities.length > 0 && (
              <div className="mt-6">
                <h4 className="text-md font-semibold text-gray-900 mb-2">Imported Entities</h4>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Name</th>
                        <th>Original ID</th>
                        <th>Supervisor</th>
                        <th>Customer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((importResult as any).imported_entities as ImportedEntity[]).map((e, idx) => (
                        <tr key={idx}>
                          <td>{e.entity_type}</td>
                          <td>{e.name || '-'}</td>
                          <td>{e.original_id ?? '-'}</td>
                          <td>{e.supervisor ?? '-'}</td>
                          <td>
                            {e.customer_id ? (
                              <a href={`/customers/${e.customer_id}`} className="text-blue-600 hover:underline">
                                View customer
                              </a>
                            ) : (
                              <span className="text-gray-500">No standalone customer</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Errors */}
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-red-900 mb-2">Errors:</h4>
                <ul className="list-disc list-inside space-y-1">
                  {importResult.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-700">{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      );
    } catch (error) {
      console.error('Error rendering import result:', error, importResult);
      return (
        <div className="mt-6 p-6 rounded-lg bg-red-50 border border-red-200">
          <div className="flex items-start">
            <XCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900">
                Error displaying import results
              </h3>
              <p className="text-sm text-red-700 mt-2">
                There was an error displaying the import results. Please check the console for details.
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Data Import</h1>
        <p className="mt-2 text-gray-600">
          Import supervisor and controller data with automatic contract creation
        </p>
      </div>

      {/* Contract Creation Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Automatic Contract Creation:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Supervisors:</strong> Creates 1 contract with 1 supervisor dashboard line + N controller lines</li>
              <li><strong>Sole Controllers:</strong> Creates 1 contract with 1 controller line</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Server Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Server className="w-5 h-5 mr-2" />
          Select Target Server
        </h2>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose the server where data will be imported:
          </label>
          <ServerDropdown
            value={selectedServerId}
            onChange={setSelectedServerId}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
            placeholder="Select a server..."
          />
          <p className="text-sm text-gray-500 mt-1">
            Data will be imported to the selected server with the appropriate server ID.
          </p>
        </div>
      </div>

      {/* Import Type Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Select Import Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <button
            onClick={() => setImportType('controller')}
            className={`p-4 rounded-lg border-2 transition-all ${
              importType === 'controller' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <FileJson className="w-8 h-8 mb-2 mx-auto text-blue-600" />
            <h3 className="font-semibold">Controller Data</h3>
            <p className="text-sm text-gray-600 mt-1">Import from controller.json</p>
          </button>

          <button
            onClick={() => setImportType('supervisor')}
            className={`p-4 rounded-lg border-2 transition-all ${
              importType === 'supervisor' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <FileJson className="w-8 h-8 mb-2 mx-auto text-blue-600" />
            <h3 className="font-semibold">Supervisor Data</h3>
            <p className="text-sm text-gray-600 mt-1">Import from supervisor.json</p>
          </button>

          <button
            onClick={() => setImportType('current-supervisor')}
            className={`p-4 rounded-lg border-2 transition-all ${
              importType === 'current-supervisor' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Building className="w-8 h-8 mb-2 mx-auto text-purple-600" />
            <h3 className="font-semibold">Current Supervisor</h3>
            <p className="text-sm text-gray-600 mt-1">Import from current_supervisor.json</p>
          </button>

          <button
            onClick={() => setImportType('file')}
            className={`p-4 rounded-lg border-2 transition-all ${
              importType === 'file' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Upload className="w-8 h-8 mb-2 mx-auto text-blue-600" />
            <h3 className="font-semibold">Upload File</h3>
            <p className="text-sm text-gray-600 mt-1">Upload custom JSON file</p>
          </button>

          <button
            onClick={() => setImportType('webhook')}
            className={`p-4 rounded-lg border-2 transition-all ${
              importType === 'webhook' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Webhook className="w-8 h-8 mb-2 mx-auto text-blue-600" />
            <h3 className="font-semibold">Webhook Import</h3>
            <p className="text-sm text-gray-600 mt-1">Paste JSON data directly</p>
          </button>
        </div>
      </div>

      {/* Import Action */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Import Data</h2>
        
        {!selectedServerId && (
          <div className="mb-4 p-4 bg-yellow-50 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-900">
                Please select a target server before importing data.
              </p>
            </div>
          </div>
        )}
        
        {importType === 'controller' && (
          <div>
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <FileText className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900">
                    This will import data from <code className="bg-blue-100 px-1 py-0.5 rounded">backend/data/controller.json</code>
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    Creates contracts for sole controllers with one contract line each.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleImportControllerJson}
              disabled={isLoading || !selectedServerId}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Importing...' : 'Import Controller Data'}
            </button>
          </div>
        )}

        {importType === 'supervisor' && (
          <div>
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <FileText className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900">
                    This will import data from <code className="bg-blue-100 px-1 py-0.5 rounded">backend/data/supervisor.json</code>
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    Creates one contract with supervisor dashboard line plus lines for each controller.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleImportSupervisorJson}
              disabled={isLoading || !selectedServerId}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Importing...' : 'Import Supervisor Data'}
            </button>
          </div>
        )}

        {importType === 'current-supervisor' && (
          <div>
            <div className="mb-4 p-4 bg-purple-50 rounded-lg">
              <div className="flex items-start">
                <Building className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-purple-900">
                    This will import data from <code className="bg-purple-100 px-1 py-0.5 rounded">backend/data/current_supervisor.json</code>
                  </p>
                  <p className="text-sm text-purple-700 mt-1">
                    Imports multiple supervisors with their controllers. Creates one contract per supervisor with dashboard + controller lines.
                  </p>
                  <p className="text-sm text-purple-700 mt-1">
                    <strong>Format:</strong> Array of objects with supervisor and controllers data.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleImportCurrentSupervisorJson}
              disabled={isLoading || !selectedServerId}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Importing...' : 'Import Current Supervisor Data'}
            </button>
          </div>
        )}

        {importType === 'file' && (
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select JSON File
              </label>
              <input
                type="file"
                accept=".json"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload a JSON file with the same structure as controller.json or supervisor.json
              </p>
            </div>
            <button
              onClick={handleFileUpload}
              disabled={isLoading || !selectedFile || !selectedServerId}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Uploading...' : 'Upload and Import'}
            </button>
          </div>
        )}

        {importType === 'webhook' && (
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                JSON Data
              </label>
              <textarea
                value={webhookData}
                onChange={(e) => {
                  setWebhookData(e.target.value);
                  setWebhookError('');
                }}
                rows={10}
                className={`block w-full text-sm text-gray-900 border rounded-lg p-3 font-mono ${
                  webhookError ? 'border-red-300' : 'border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder='{"supervisor": {...}, "user": {...}, "controllers": [...]}'
              />
              {webhookError && (
                <p className="mt-1 text-sm text-red-600">{webhookError}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Paste JSON data in supervisor or controller format
              </p>
            </div>
            <button
              onClick={handleWebhookImport}
              disabled={isLoading || !webhookData.trim() || !selectedServerId}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Importing...' : 'Import via Webhook'}
            </button>
          </div>
        )}
      </div>

      {/* Import Result */}
      {renderImportResult()}

      {/* Data Structure Information */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-3">Expected Data Structure</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Supervisor JSON Structure:</h4>
            <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-x-auto">
{`{
  "supervisor": {
    "id": 22,
    "display_name": "Mystic-Demo",
    "plan_name": "pro",
    "clients_paid_for": 250,
    ...
  },
  "user": {
    "id": 26,
    "username": "user@example.com",
    "email": "user@example.com",
    ...
  },
  "controllers": [
    {
      "controller_id": 309,
      "controller_name": "Controller 1",
      "plan_name": "basic",
      ...
    }
  ]
}`}
            </pre>
            <p className="text-sm text-gray-600 mt-2">
              Creates 1 contract with supervisor dashboard + controller lines
            </p>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Controller JSON Structure:</h4>
            <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-x-auto">
{`{
  "user": {
    "id": 100,
    "username": "controller@example.com",
    "email": "controller@example.com",
    ...
  },
  "controllers": [
    {
      "controller_id": 500,
      "controller_name": "Standalone Controller",
      "plan_name": "basic",
      ...
    }
  ]
}`}
            </pre>
            <p className="text-sm text-gray-600 mt-2">
              Creates 1 contract with 1 controller line
            </p>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Current Supervisor JSON Structure:</h4>
            <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-x-auto">
{`[
  {
    "supervisor": {
      "id": 163,
      "display_name": "Jasmin Lieffering",
      "plan_name": "pro",
      "clients_paid_for": 6,
      ...
    },
    "controllers": [
      {
        "controller_id": 5056,
        "controller_name": "b'nerd GmbH",
        "supervisor": 163,
        "plan_name": "supervisor-client",
        ...
      }
    ]
  }
]`}
            </pre>
            <p className="text-sm text-gray-600 mt-2">
              Array format: Creates 1 contract per supervisor with dashboard + controller lines
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataImport;